import React, { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { AnalysisSectionHeader } from '../../../../../AnalysisSectionHeader'
import {
  AnalysisMetricsGrid,
  AnalysisMetricCard,
  MetricHeader,
  MetricIcon,
  MetricTitle,
  MetricValue,
  MetricSubtext,
} from '../../../../../atoms/AnalysisMetricCard'
import { InfoTooltip } from '../../../../../InfoTooltip'
import { TooltipMetricExplanation } from '../../../../../TooltipMetricExplanation'
import { AnalysisTabContentContainer } from '../../../../../molecules/SectionAnalysisStyles'
import { Icon } from '../../../../../atoms/Icon'
import { Box } from '../../../../../atoms/Box'
import { formatNumberSwiss } from '../../../../../../utils/number/formatUtils'
import { useAnalysisMode } from '../../../contexts/AnalysisModeContext'
import { EntranceCard } from './components'
import { processBuildingData, calculateSummary, toggleSetItem } from './utils'
import { Container, NoDataMessage } from './UnitDetailsTab.styles'

/**
 * Shared UnitDetailsTab for analysis views.
 * Displays building administrative data (entrances → flat table with floor separators).
 * Read-only mode with price predictions. Matches main app design.
 * Reads entity data from AnalysisModeContext.
 */
export const UnitDetailsTab: React.FC = () => {
  const { t } = useTranslation('buildings')
  const { entity } = useAnalysisMode()

  const processedData = useMemo(() => {
    if (!entity) return null
    return processBuildingData(entity, t)
  }, [entity, t])

  const [expandedEntrances, setExpandedEntrances] = useState<Set<string>>(
    new Set()
  )

  // Expand all entrances by default when data loads
  React.useEffect(() => {
    if (processedData && processedData.length > 0) {
      setExpandedEntrances(new Set(processedData.map((e) => e.id)))
    }
  }, [processedData])

  const summary = useMemo(() => {
    if (!entity) return null
    return calculateSummary(entity, processedData)
  }, [entity, processedData])

  const toggleEntrance = (entranceId: string) => {
    setExpandedEntrances(toggleSetItem(expandedEntrances, entranceId))
  }

  if (!entity) {
    return <NoDataMessage>{t('unitDetails.states.noBuilding')}</NoDataMessage>
  }

  return (
    <Container>
      <AnalysisSectionHeader
        title={t('unitDetails.title')}
        subtitle={t('unitDetails.subtitle')}
      />
      <AnalysisTabContentContainer>
        {summary && (
          <AnalysisMetricsGrid>
            <AnalysisMetricCard>
              <MetricHeader>
                <MetricIcon color="#f59e0b">
                  <Icon name="ruler" size="small" />
                </MetricIcon>
                <MetricTitle>
                  {t('unitDetails.metrics.dwellingArea')}
                  <InfoTooltip>
                    <TooltipMetricExplanation
                      title={t('unitDetails.tooltips.dwellingArea.title')}
                      shortDescription={t(
                        'unitDetails.tooltips.dwellingArea.description'
                      )}
                      source={t('unitDetails.tooltips.dwellingArea.source')}
                    />
                  </InfoTooltip>
                </MetricTitle>
              </MetricHeader>
              <Box style={{ marginTop: '8px' }}>
                <MetricValue>
                  {summary.dwellingArea} {t('unitDetails.common.squareMeters')}
                </MetricValue>
                <MetricSubtext>
                  {t('unitDetails.metrics.units')}: {summary.units}
                </MetricSubtext>
              </Box>
            </AnalysisMetricCard>

            {summary.yearlyRentalMin > 0 && (
              <AnalysisMetricCard>
                <MetricHeader>
                  <MetricIcon color="#3b82f6">
                    <Icon name="wallet" size="small" />
                  </MetricIcon>
                  <MetricTitle>
                    {t('unitDetails.metrics.rentalValue')}
                    <InfoTooltip>
                      <TooltipMetricExplanation
                        title={t('unitDetails.tooltips.rentalValue.title')}
                        shortDescription={t(
                          'unitDetails.tooltips.rentalValue.description'
                        )}
                        calculation={t(
                          'unitDetails.tooltips.rentalValue.calculation'
                        )}
                        source={t('unitDetails.tooltips.rentalValue.source')}
                      />
                    </InfoTooltip>
                  </MetricTitle>
                </MetricHeader>
                <Box style={{ marginTop: '8px' }}>
                  <MetricValue>
                    CHF{' '}
                    {formatNumberSwiss(
                      Math.round(summary.yearlyRentalMin / 1000)
                    )}
                    K -{' '}
                    {formatNumberSwiss(
                      Math.round(summary.yearlyRentalMax / 1000)
                    )}
                    K
                  </MetricValue>
                </Box>
              </AnalysisMetricCard>
            )}

            {summary.purchaseMin > 0 && (
              <AnalysisMetricCard>
                <MetricHeader>
                  <MetricIcon color="#10b981">
                    <Icon name="home" size="small" />
                  </MetricIcon>
                  <MetricTitle>
                    {t('unitDetails.metrics.purchaseValue')}
                    <InfoTooltip>
                      <TooltipMetricExplanation
                        title={t('unitDetails.tooltips.purchaseValue.title')}
                        shortDescription={t(
                          'unitDetails.tooltips.purchaseValue.description'
                        )}
                        calculation={t(
                          'unitDetails.tooltips.purchaseValue.calculation'
                        )}
                        source={t('unitDetails.tooltips.purchaseValue.source')}
                      />
                    </InfoTooltip>
                  </MetricTitle>
                </MetricHeader>
                <Box style={{ marginTop: '8px' }}>
                  <MetricValue>
                    CHF{' '}
                    {formatNumberSwiss(Math.round(summary.purchaseMin / 1000))}K
                    -{' '}
                    {formatNumberSwiss(Math.round(summary.purchaseMax / 1000))}K
                  </MetricValue>
                </Box>
              </AnalysisMetricCard>
            )}
          </AnalysisMetricsGrid>
        )}

        {processedData && processedData.length > 0 ? (
          processedData.map((entrance) => (
            <EntranceCard
              key={entrance.id}
              entrance={entrance}
              expanded={expandedEntrances.has(entrance.id)}
              onToggleEntrance={toggleEntrance}
              t={t}
            />
          ))
        ) : (
          <NoDataMessage>{t('unitDetails.states.noUnits')}</NoDataMessage>
        )}
      </AnalysisTabContentContainer>
    </Container>
  )
}
